import React, { useMemo, useState } from "react";
import { Row, Col, Pagination } from "react-bootstrap";
import { PlusCircleFill, DashCircleFill } from "react-bootstrap-icons";
import '../../assets/css/mydashboard.css';

const DetailedTransactionTable = ({
  onUtidClick,
  detailedData,
  terminalType,
  currentPage,
  totalRecords,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  months = [], // Months from API
}) => {
  const [expandedRowUtid, setExpandedRowUtid] = useState(null);
  const [isTableExpanded, setIsTableExpanded] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalRecords / rowsPerPage));
  const startRecord = (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, totalRecords);

  // Get month labels from API or calculate fallback
  const monthLabels = useMemo(() => {
    if (months && months.length >= 3) {
      return {
        current: months[0] || "",
        prev: months[1] || "",
        prevPrev: months[2] || "",
      };
    }
    
    // Fallback calculation if API months not available yet
    const options = { month: "short" };
    const today = new Date();
    const year = today.getFullYear().toString().slice(-2);

    const current =
      today.toLocaleString("en-US", options) + "-" + year;

    const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const prevYear = prevMonth.getFullYear().toString().slice(-2);
    const prev =
      prevMonth.toLocaleString("en-US", options) + "-" + prevYear;

    const prevPrevMonth = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    const prevPrevYear = prevPrevMonth.getFullYear().toString().slice(-2);
    const prevPrev =
      prevPrevMonth.toLocaleString("en-US", options) + "-" + prevPrevYear;

    return {
      current: current,
      prev: prev,
      prevPrev: prevPrev,
    };
  }, [months]);

  // Calculate pagination page numbers
  const pageNumbers = [];
  const visiblePages = 4; // show up to 4 pages at a time
  let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
  let endPage = Math.min(totalPages, startPage + visiblePages - 1);
  if (endPage - startPage < visiblePages - 1) {
    startPage = Math.max(1, endPage - visiblePages + 1);
  }
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  // Handle row expansion/collapse by UTID
  const handleRowToggle = (utid) => {
    if (expandedRowUtid === utid) {
      setExpandedRowUtid(null);
    } else {
      setExpandedRowUtid(utid);
    }
    setIsTableExpanded(true);
  };

  return (
    <Row>
      <Col md={12}>
        <div className="content-tdetails-table table-full-width">
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "40px" }}></th> {/* Icon */}
                  <th>UTID</th>
                  <th>Store Name</th>
                </tr>
              </thead>
              <tbody>
                {detailedData && detailedData.length > 0 ? (
                  detailedData.map((row) => {
                    const isExpanded = expandedRowUtid === row.utid;
                    return (
                      <React.Fragment key={row.utid}>
                        {/* Main Row */}
                        <tr>
                          <td className="text-center">
                            {isExpanded ? (
                              <DashCircleFill
                                className="expand-icon icon-minus"
                                onClick={() => handleRowToggle(row.utid)}
                              />
                            ) : (
                              <PlusCircleFill
                                className="expand-icon icon-plus"
                                onClick={() => handleRowToggle(row.utid)}
                              />
                            )}
                          </td>
                          {/* UTID is clickable */}
                          <td
                            onClick={() => onUtidClick(row.utid)}
                            className="clickable-store-name"
                          >
                            {row.utid}
                          </td>
                          <td>{row.storeName}</td>
                        </tr>
                        {/* Expanded Details */}
                        {isExpanded && (
                          <>
                            <tr className="expanded-detail-row">
                              <td></td> {/* Empty cell for icon column */}
                              <td style={{ paddingLeft: '20px', fontWeight: '600', width: '200px' }}>
                                Mobile:
                              </td>
                              <td>
                                {row.mobile || "-"}
                              </td>
                            </tr>
                            <tr className="expanded-detail-row">
                              <td></td>
                              <td style={{ paddingLeft: '20px', fontWeight: '600', width: '200px' }}>
                                City:
                              </td>
                              <td>
                                {row.city || "-"}
                              </td>
                            </tr>
                            <tr className="expanded-detail-row">
                              <td></td>
                              <td style={{ paddingLeft: '20px', fontWeight: '600', width: '200px' }}>
                                {monthLabels.current} Vol (Rs. Lakhs):
                              </td>
                              <td className="text-end">
                                {row.currentVol || "-"}
                              </td>
                            </tr>
                            <tr className="expanded-detail-row">
                              <td></td>
                              <td style={{ paddingLeft: '20px', fontWeight: '600', width: '200px' }}>
                                {monthLabels.prev} Vol (Rs. Lakhs):
                              </td>
                              <td className="text-end">
                                {row.m1Vol || "-"}
                              </td>
                            </tr>
                            <tr className="expanded-detail-row">
                              <td></td>
                              <td style={{ paddingLeft: '20px', fontWeight: '600', width: '200px' }}>
                                {monthLabels.prevPrev} Vol (Rs. Lakhs):
                              </td>
                              <td className="text-end">
                                {row.m2Vol || "-"}
                              </td>
                            </tr>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center py-3">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalRecords > 0 && (
            <div className="pagination-wrapper">
              {/* Left: Record summary */}
{/*               <div className="pagination-summary"> */}
{/*                 Showing{" "} */}
{/*                 <strong> */}
{/*                   {startRecord}–{endRecord} */}
{/*                 </strong>{" "} */}
{/*                 of <strong>{totalRecords}</strong> records */}
{/*               </div> */}

              {/* Middle: Pagination buttons with onClick guards */}
              <Pagination size="sm">
                <Pagination.First
                  onClick={() => {
                    if (currentPage > 1) onPageChange(1);
                  }}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => {
                    if (currentPage > 1) onPageChange(currentPage - 1);
                  }}
                  disabled={currentPage === 1}
                />

                {/* Show first page and ellipsis if needed */}
                {startPage > 1 && (
                  <>
                    <Pagination.Item onClick={() => onPageChange(1)}>
                      1
                    </Pagination.Item>
                    {startPage > 2 && <Pagination.Ellipsis disabled />}
                  </>
                )}

                {/* Page numbers */}
                {pageNumbers.map((num) => (
                  <Pagination.Item
                    key={num}
                    active={num === currentPage}
                    onClick={() => onPageChange(num)}
                  >
                    {num}
                  </Pagination.Item>
                ))}

                {/* Show last page and ellipsis if needed */}
                {endPage < totalPages && (
                  <>
                    {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
                    <Pagination.Item onClick={() => onPageChange(totalPages)}>
                      {totalPages}
                    </Pagination.Item>
                  </>
                )}

                <Pagination.Next
                  onClick={() => {
                    if (currentPage < totalPages) onPageChange(currentPage + 1);
                  }}
                  disabled={currentPage >= totalPages}
                />
                <Pagination.Last
                  onClick={() => {
                    if (currentPage < totalPages) onPageChange(totalPages);
                  }}
                  disabled={currentPage >= totalPages}
                />
              </Pagination>
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default DetailedTransactionTable;